import React from 'react';
import { TruckIcon } from '@heroicons/react/24/outline';
import { StatusBadge } from '../../../../shared/components/badges/StatusBadge';
import TabbedForm from '../../../../shared/components/forms/FormTabs';
import VaccineDispatchExpandRow from '../components/VaccineDispatchExpandRow';
import { VaccineDispatchService } from '../../../../shared/services/vaccineDispatchService';
import apiService from '../../../../shared/services/apiService';

// -------------------------
// Option Loaders (using dropdowns API)
// -------------------------

// Vaccine Options from dropdowns API
export const getVaccineOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns/vaccines');
    const vaccines =
      res?.data?.vaccines ||
      res?.vaccines ||
      res?.data ||
      [];

    return vaccines.map(v => ({
      value: v.value || v.id || v._id,
      label: v.label || v.name
    }));
  } catch (error) {
    console.warn('Failed to load vaccines from dropdowns API', error);
    return [];
  }
};

// Center Options from dropdowns API (for From/To Location selects)
export const getCenterOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns/vaccination-centers?isAdmin=true');
    const centers =
      res?.data?.vaccinationCenters ||
      res?.vaccinationCenters ||
      res?.data ||
      [];

    return centers.map(c => ({
      value: c.value || c.id || c._id,
      label: c.label || c.name
    }));
  } catch (error) {
    console.warn('Failed to load centers from dropdowns API', error);
    return [];
  }
};

// Transfer Status Options for form dropdown (transferStatuses from dropdowns API)
export const getTransferStatusOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns');
    const statuses =
      res?.data?.data?.transferStatuses ||
      res?.data?.transferStatuses ||
      res?.transferStatuses ||
      [];

    if (statuses.length === 0) {
      console.warn('No transfer statuses found in API response');
    }

    return statuses
      .filter(s => s.value !== 'ALL')
      .map(s => ({
        value: s.value,
        label: s.label
      }));
  } catch (error) {
    console.warn('Failed to load transfer statuses from dropdowns API', error);
    return [
      { value: 'DISPATCHED', label: 'Dispatched' },
      { value: 'IN_TRANSIT', label: 'In Transit' },
      { value: 'RECEIVED', label: 'Received' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'CANCELLED', label: 'Cancelled' }
    ];
  }
};

// Delivery Status Options for Status field (deliveryStatuses from dropdowns API)
export const getDeliveryStatusOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns');
    const statuses =
      res?.data?.data?.deliveryStatuses ||
      res?.data?.deliveryStatuses ||
      res?.deliveryStatuses ||
      [];

    if (statuses.length === 0) {
      console.warn('No delivery statuses found in API response');
    }

    return statuses
      .filter(s => s.value !== 'ALL')
      .map(s => ({
        value: s.value,
        label: s.label
      }));
  } catch (error) {
    console.warn('Failed to load delivery statuses from dropdowns API', error);
    return [
      { value: 'ORDERED', label: 'Ordered' },
      { value: 'PACKED', label: 'Packed' },
      { value: 'DISPATCHED', label: 'Dispatched' },
      { value: 'IN_TRANSIT', label: 'In Transit' },
      { value: 'ARRIVED', label: 'Arrived' }
    ];
  }
};

// Transfer Status Options for filter dropdown (transferStatuses from dropdowns API)
export const getTransferFilterStatusOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns');
    const statuses =
      res?.data?.data?.transferStatuses ||
      res?.data?.transferStatuses ||
      res?.transferStatuses ||
      [];

    return statuses
      .filter(s => s.value !== 'ALL')
      .map(s => ({
        value: s.value,
        label: s.label
      }));
  } catch (error) {
    console.warn('Failed to load transfer filter statuses from dropdowns API', error);
    return [
      { value: 'DISPATCHED', label: 'Dispatched' },
      { value: 'IN_TRANSIT', label: 'In Transit' },
      { value: 'RECEIVED', label: 'Received' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'CANCELLED', label: 'Cancelled' }
    ];
  }
};

export const getSupplierdetailsOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns/suppliers');

    const suppliers =
      res?.data?.suppliers ||
      res?.data?.data?.suppliers ||
      res?.data ||
      [];

    return suppliers.map(s => ({
      value: s._id || s.id,
      label: s.supplierName || s.name
    }));
  } catch (error) {
    console.error('Failed to load suppliers', error);
    return [];
  }
};

// Quality Check Options
export const getQualityCheckOptions = () => [
  { value: 'PENDING', label: 'Pending' },
  { value: 'PASSED', label: 'Passed' },
  { value: 'FAILED', label: 'Failed' }
];

// Medical Staff Options for Received By (with clinicId from toLocationId)
export const getReceivedByOptions = async (formData) => {
  try {
    const clinicId = formData?.toLocationId || null;
    const res = await VaccineDispatchService.getMedicalStaffDropdown(clinicId);
    const staff = res?.data?.medicalStaff || res?.medicalStaff || res?.data || [];
    return staff.map(s => ({
      value: s.id || s._id,
      label: s.fullName || s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim()
    }));
  } catch (error) {
    console.warn('Failed to load medical staff for Received By', error);
    return [];
  }
};

// Medical Staff Options for Inspected By
export const getInspectedByOptions = async () => {
  try {
    const res = await VaccineDispatchService.getMedicalStaffDropdown();
    const staff = res?.data?.medicalStaff || res?.medicalStaff || res?.data || [];
    return staff.map(s => ({
      value: s.id || s._id,
      label: s.fullName || s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim()
    }));
  } catch (error) {
    console.warn('Failed to load medical staff for Inspected By', error);
    return [];
  }
};

// -------------------------
// Vaccine Dispatch Config
// -------------------------
export const vaccineDispatchConfig = {
  title: 'Vaccine Transfer',
  endpoint: 'admin/vaccine-transfers',
  crudEndpoint: 'vaccine-transfers',
  icon: TruckIcon,
  useUrlNavigation: true,
  basePath: '/admin/vaccine-Transfer',

  // Enable multi-step form submission
  multiStepSubmission: true,

  statusOptionsLoader: getTransferFilterStatusOptions,

  // -------------------------
  // Grid Columns
  // -------------------------
  columns: [
    {
      key: 'vaccineName',
      label: 'Vaccine',
      render: (item) => item.vaccine?.name || item.vaccineName || '—'
    },
    {
      key: 'fromCenterName',
      label: 'From',
      render: item => item.fromLocation?.name || item.fromCenterName || '—'
    },
    {
      key: 'toCenterName',
      label: 'To',
      render: item => item.toLocation?.name || item.toCenterName || '—'
    },
    {
      key: 'status',
      label: 'Status',
      render: item => <StatusBadge status={item.status || 'pending'} />
    }
  ],

  // -------------------------
  // Form Fields - All API Fields
  // -------------------------
  formFields: [
    // ============ VACCINE INFO SECTION ============

    {
      name: 'vaccineId',
      label: 'Vaccine Name',
      type: 'select',
      optionsLoader: getVaccineOptions,
      required: true,
      section: 'vaccine',
      renderValue: (value, item) => item?.vaccine?.name || value || '—'
    },
    {
      name: 'supplierId',
      label: 'Manufacturer/Supplier',
      type: 'select',
      optionsLoader: getSupplierdetailsOptions,
      required: true,
      section: 'vaccine',
      renderValue: (value, item) =>
        item?.supplierName ||
        item?.manufacturer?.supplierName ||
        '—'
    },
    {
      name: 'batchNumber',
      label: 'Batch Number',
      type: 'text',
      required: true,
      section: 'vaccine'
    },
    {
      name: 'quantityDispatched',
      label: 'Quantity Dispatched',
      type: 'number',
      required: true,
      min: 1,
      section: 'vaccine'
    },
    {
      name: 'manufacturingDate',
      label: 'Manufacturing Date',
      type: 'date',
      required: true,
      section: 'vaccine',
      renderValue: (value) => value
        ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—'
    },
    {
      name: 'expiryDate',
      label: 'Expiry Date',
      type: 'date',
      required: true,
      section: 'vaccine',
      renderValue: (value) => value
        ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—'
    },
    {
      name: 'price',
      label: 'Price per Unit',
      type: 'number',
      required: true,
      min: 0,
      section: 'vaccine'
    },
    {
      name: 'totalAmount',
      label: 'Total Amount',
      type: 'number',
      section: 'vaccine',
      readOnly: true,
      min: 0,
      renderValue: (value) => value ? `₹${parseFloat(value).toLocaleString('en-IN')}` : '—'
    },
    {
      name: 'invoiceNo',
      label: 'Invoice Number',
      type: 'text',
      section: 'vaccine',
      placeholder: 'Enter invoice number'
    },

    // ============ ROUTE INFO SECTION ============
    {
      name: 'fromLocationId',
      label: 'From Location',
      type: 'select',
      optionsLoader: getCenterOptions,
      required: true,
      section: 'route',
      renderValue: (value, item) => item?.fromLocation?.name || value || '—'
    },
    {
      name: 'toLocationId',
      label: 'To Location',
      type: 'select',
      optionsLoader: getCenterOptions,
      required: true,
      section: 'route',
      renderValue: (value, item) => item?.toLocation?.name || value || '—',
      // Validation: To Location cannot be the same as From Location
      validate: (value, context) => {
        if (!value) return true; // Let required validation handle empty
        const fromLocationId = context?.parent?.fromLocationId;
        return value !== fromLocationId;
      },
      validationMessage: 'To Location cannot be the same as From Location'
    },
    {
      name: 'dispatchedOn',
      label: 'Dispatched On',
      type: 'date',
      section: 'route',
      renderValue: (value) => value
        ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—'
    },
    {
      name: 'expectedDeliveryDate',
      label: 'Expected Delivery Date',
      type: 'date',
      required: true,
      section: 'route',
      renderValue: (value) => value
        ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—'
    },
    {
      name: 'courierName',
      label: 'Courier Name',
      type: 'text',
      section: 'route',
      placeholder: 'Enter courier/transporter name'
    },
    {
      name: 'trackingNumber',
      label: 'Tracking Number',
      type: 'text',
      section: 'route',
      placeholder: 'Enter tracking number'
    },

    // ============ HANDLING & STORAGE SECTION ============
    {
      name: 'packagingType',
      label: 'Packaging Type',
      type: 'text',
      section: 'handling',
      placeholder: 'e.g. Cold box, Thermocol'
    },
    {
      name: 'storageCondition',
      label: 'Storage Condition',
      type: 'text',
      placeholder: 'e.g. 2–8°C',
      section: 'handling'
    },
    {
      name: 'temperatureAtDispatch',
      label: 'Temperature at Dispatch (°C)',
      type: 'text',
      section: 'handling',
      placeholder: 'e.g. 4 or -20',
      renderValue: (value) => value !== null && value !== undefined && value !== '' ? `${value}°C` : '—'
    },
    {
      name: 'coldChainMaintained',
      label: 'Cold Chain Maintained',
      type: 'checkbox',
      section: 'handling',
      renderValue: (value) => value ? 'Yes' : 'No'
    },
    {
      name: 'transferStatus',
      label: 'Transfer Status',
      type: 'select',
      optionsLoader: getDeliveryStatusOptions,
      section: 'handling',
      renderValue: (value) => value || '—'
    },
    {
      name: 'status',
      label: 'Delivery Status',
      type: 'select',
      optionsLoader: getTransferStatusOptions,
      default: 'DISPATCHED',
      section: 'handling'
    },
    {
      name: 'dispatchRemarks',
      label: 'Dispatch Remarks',
      type: 'textarea',
      rows: 3,
      section: 'handling',
      fullWidth: true
    },

    // ============ RECEIVE SECTION ============
    {
      name: 'receivedOn',
      label: 'Received On',
      type: 'date',
      section: 'receive',
      renderValue: (value) => value
        ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—'
    },
    {
      name: 'receivedBy',
      label: 'Received By',
      type: 'select',
      optionsLoader: getReceivedByOptions,
      section: 'receive',
      placeholder: 'Select person who received',
      dependsOn: 'toLocationId',
      renderValue: (value, item) => item?.receiver?.fullName || value || '—'
    },
    {
      name: 'quantityReceived',
      label: 'Quantity Received',
      type: 'number',
      section: 'receive',
      min: 0
    },
    {
      name: 'quantityAccepted',
      label: 'Quantity Accepted',
      type: 'number',
      section: 'receive',
      min: 0
    },
    {
      name: 'quantityRejected',
      label: 'Quantity Rejected',
      type: 'number',
      section: 'receive',
      min: 0
    },
    {
      name: 'temperatureAtReceive',
      label: 'Temperature at Receive (°C)',
      type: 'text',
      section: 'receive',
      placeholder: 'e.g. 4 or -20',
      renderValue: (value) => value !== null && value !== undefined && value !== '' ? `${value}°C` : '—'
    },
    {
      name: 'qualityCheck',
      label: 'Quality Check',
      type: 'select',
      options: getQualityCheckOptions(),
      section: 'receive',
      renderValue: (value) => {
        const labels = { PENDING: 'Pending', PASSED: 'Passed', FAILED: 'Failed' };
        return labels[value] || value || '—';
      }
    },
    {
      name: 'receiveRemarks',
      label: 'Receive Remarks',
      type: 'textarea',
      rows: 3,
      section: 'receive',
      fullWidth: true
    },

    // ============ INSPECTION SECTION ============
    {
      name: 'inspectedOn',
      label: 'Inspected On',
      type: 'date',
      section: 'inspection',
      renderValue: (value) => value
        ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—'
    },
    {
      name: 'inspectedBy',
      label: 'Inspected By',
      type: 'select',
      optionsLoader: getInspectedByOptions,
      section: 'inspection',
      placeholder: 'Select inspector',
      renderValue: (value, item) => item?.inspector?.fullName || value || '—'
    },
    {
      name: 'inspectedQualityCheck',
      label: 'Inspection Quality Check',
      type: 'select',
      options: getQualityCheckOptions(),
      section: 'inspection',
      renderValue: (value) => {
        const labels = { PENDING: 'Pending', PASSED: 'Passed', FAILED: 'Failed' };
        return labels[value] || value || '—';
      }
    },
    {
      name: 'inspectedRemarks',
      label: 'Inspection Remarks',
      type: 'textarea',
      rows: 3,
      section: 'inspection',
      fullWidth: true
    }
  ],

  // -------------------------
  // Search Fields
  // -------------------------
  searchFields: ['vaccineName', 'batchNumber', 'trackingNumber', 'fromCenterId', 'toCenterId', 'courierName', 'invoiceNo', 'transferId'],
  formState: { activeTab: 'vaccine' },

  // -------------------------
  // Custom Handlers
  // -------------------------
  customHandlers: {
    renderExpandedRow: (item) => <VaccineDispatchExpandRow item={item} />,

    // Auto-calculate Total Amount = Quantity Dispatched * Price per Unit
    // Also prevent negative values for quantity and price fields
    onFieldChange: (fieldName, value, currentValues, setValues) => {
      // List of fields that should not accept negative values
      const nonNegativeFields = [
        'quantityDispatched', 'price', 'totalAmount',
        'quantityReceived', 'quantityAccepted', 'quantityRejected'
      ];

      // Prevent negative values for specified fields
      if (nonNegativeFields.includes(fieldName)) {
        const numValue = parseFloat(value);
        if (numValue < 0) {
          return true; // Block negative value, don't update
        }
      }

      // Auto-calculate total amount
      if (fieldName === 'quantityDispatched' || fieldName === 'price') {
        const quantity = fieldName === 'quantityDispatched'
          ? Math.max(0, parseFloat(value) || 0)
          : Math.max(0, parseFloat(currentValues.quantityDispatched) || 0);
        const price = fieldName === 'price'
          ? Math.max(0, parseFloat(value) || 0)
          : Math.max(0, parseFloat(currentValues.price) || 0);

        const totalAmount = quantity * price;

        setValues({
          ...currentValues,
          [fieldName]: value,
          totalAmount: totalAmount > 0 ? totalAmount : ''
        });

        return true; // Indicate that we handled the change
      }
      return false; // Let default handler process other fields
    },

    transformData: (response) => {
      const items =
        response?.data?.transfers ||
        response?.data?.data?.transfers ||
        [];

      return items.map(item => ({
        ...item,
        fromCenterName: item.fromLocation?.name || '—',
        toCenterName: item.toLocation?.name || '—'
      }));
    },

    onView: async (item) => {
      try {
        const response = await VaccineDispatchService.getById(item.id || item._id);
        const data = response?.data || response;

        // ---- Vaccine
        if (data.vaccine) {
          data.vaccineId = data.vaccine.id || data.vaccine._id;
          data.vaccineName = data.vaccine.name;
        }

        // ---- Locations
        if (data.fromLocation) {
          data.fromLocationId = data.fromLocation.id || data.fromLocation._id;
          data.fromLocationName = data.fromLocation.name;
        }

        if (data.toLocation) {
          data.toLocationId = data.toLocation.id || data.toLocation._id;
          data.toLocationName = data.toLocation.name;
        }

        // ---- Supplier / Manufacturer
        if (data.manufacturer) {
          data.supplierId = data.manufacturer.id || data.manufacturer._id;
          data.supplierName = data.manufacturer.supplierName;
        } else if (data.supplier) {
          data.supplierId = data.supplier.id || data.supplier._id;
          data.supplierName = data.supplier.supplierName;
        }

        // ---- Received By / Inspected By
        if (data.receivedByStaff) {
          data.receivedBy = data.receivedByStaff.id || data.receivedByStaff._id;
          data.receivedByName = data.receivedByStaff.fullName || data.receivedByStaff.name;
        }

        if (data.inspectedByStaff) {
          data.inspectedBy = data.inspectedByStaff.id || data.inspectedByStaff._id;
          data.inspectedByName = data.inspectedByStaff.fullName || data.inspectedByStaff.name;
        }

        return data;
      } catch (error) {
        console.error('Failed to fetch dispatch details:', error);
        return item;
      }
    },

    renderFormSections: (formFields, formData, handleFormChange, mode, renderFormField, activeTab, setActiveTab, transferId) => {
      const tabsConfig = {
        vaccine: { label: 'Vaccine Info' },
        route: { label: 'Route & Shipping' },
        handling: { label: 'Handling & Storage' },
        receive: { label: 'Receive Info' },
        inspection: { label: 'Inspection' }
      };

      // Disable tabs based on mode and transferStatus
      const disabledTabs = [];

      // In add mode without transferId: disable receive and inspection
      if (mode === 'add' && !transferId) {
        disabledTabs.push('receive', 'inspection');
      }
      // If transferStatus is not ARRIVED, disable inspection tab
      else if (formData?.transferStatus !== 'ARRIVED') {
        disabledTabs.push('inspection');
      }

      return (
        <TabbedForm
          formFields={formFields}
          formData={formData}
          handleFormChange={handleFormChange}
          renderFormField={renderFormField}
          tabsConfig={tabsConfig}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          disabledTabs={disabledTabs}
        />
      );
    },

    // Multi-step tab submission handler
    // Called when user clicks "Next" on certain tabs
    onTabSubmit: async (currentTab, nextTab, formData, mode, transferId) => {
      // Use update if we have transferId (either edit mode OR already created in add mode)
      const shouldUpdate = !!transferId;

      // Helper function to format date to ISO string
      const formatDateToISO = (dateValue) => {
        if (!dateValue) return null;
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date.toISOString();
      };

      // Helper function to build dispatch payload
      const buildDispatchPayload = (formData) => {
        const quantity = parseInt(formData.quantityDispatched) || 0;
        const price = parseFloat(formData.price) || 0;
        const totalAmount = quantity * price;

        const payload = {};

        // Add required fields only if they have values
        if (formData.vaccineId) payload.vaccineId = formData.vaccineId;
        if (formData.supplierId) payload.supplierId = formData.supplierId;
        if (formData.batchNumber) payload.batchNumber = formData.batchNumber;
        if (quantity > 0) payload.quantityDispatched = quantity;
        if (formData.fromLocationId) payload.fromLocationId = formData.fromLocationId;
        if (formData.toLocationId) payload.toLocationId = formData.toLocationId;
        if (formData.expectedDeliveryDate) payload.expectedDeliveryDate = formatDateToISO(formData.expectedDeliveryDate);
        if (formData.expiryDate) payload.expiryDate = formatDateToISO(formData.expiryDate);
        if (formData.manufacturingDate) payload.manufacturingDate = formatDateToISO(formData.manufacturingDate);
        if (price > 0) payload.price = price;
        if (totalAmount > 0) payload.totalAmount = totalAmount;

        // Add optional fields
        if (formData.packagingType) payload.packagingType = formData.packagingType;
        if (formData.storageCondition) payload.storageCondition = formData.storageCondition;
        if (formData.dispatchRemarks) payload.dispatchRemarks = formData.dispatchRemarks;
        if (formData.dispatchedOn) payload.dispatchedOn = formatDateToISO(formData.dispatchedOn);
        if (formData.courierName) payload.courierName = formData.courierName;
        if (formData.trackingNumber) payload.trackingNumber = formData.trackingNumber;
        if (formData.temperatureAtDispatch) payload.temperatureAtDispatch = formData.temperatureAtDispatch;
        if (formData.coldChainMaintained) payload.coldChainMaintained = formData.coldChainMaintained;
        if (formData.transferStatus) payload.transferStatus = formData.transferStatus;
        if (formData.status) payload.status = formData.status;
        if (formData.invoiceNo) payload.invoiceNo = formData.invoiceNo;

        return payload;
      };

      try {
        // Tab 1 (vaccine) -> Tab 2 (route): Update transfer in edit mode
        if (currentTab === 'vaccine' && nextTab === 'route') {
          if (!transferId) {
            // In add mode, just move to next tab without API call
            return { success: true, transferId: null };
          }
          // Edit mode: Update transfer with vaccine info
          const payload = buildDispatchPayload(formData);
          const response = await VaccineDispatchService.update(transferId, payload);
          return { success: true, data: response?.data || response, transferId };
        }

        // Tab 2 (route) -> Tab 3 (handling): Update transfer in edit mode
        if (currentTab === 'route' && nextTab === 'handling') {
          if (!transferId) {
            // In add mode, just move to next tab without API call
            return { success: true, transferId: null };
          }
          // Edit mode: Update transfer with route info
          const payload = buildDispatchPayload(formData);
          const response = await VaccineDispatchService.update(transferId, payload);
          return { success: true, data: response?.data || response, transferId };
        }

        // Tab 3 (handling) -> Tab 4 (receive): Create/Update transfer
        if (currentTab === 'handling' && nextTab === 'receive') {
          // Calculate total amount
          const quantity = parseInt(formData.quantityDispatched) || 0;
          const price = parseFloat(formData.price) || 0;
          const totalAmount = quantity * price;

          // Build payload with only non-null/non-empty values
          const dispatchPayload = {
            vaccineId: formData.vaccineId,
            supplierId: formData.supplierId,
            batchNumber: formData.batchNumber,
            quantityDispatched: quantity,
            fromLocationId: formData.fromLocationId,
            toLocationId: formData.toLocationId,
            expectedDeliveryDate: formatDateToISO(formData.expectedDeliveryDate),
            expiryDate: formatDateToISO(formData.expiryDate),
            manufacturingDate: formatDateToISO(formData.manufacturingDate),
            price: price,
            totalAmount: totalAmount > 0 ? totalAmount : null
          };

          // Add optional fields only if they have values
          if (formData.packagingType) dispatchPayload.packagingType = formData.packagingType;
          if (formData.storageCondition) dispatchPayload.storageCondition = formData.storageCondition;
          if (formData.dispatchRemarks) dispatchPayload.dispatchRemarks = formData.dispatchRemarks;
          if (formData.dispatchedOn) dispatchPayload.dispatchedOn = formatDateToISO(formData.dispatchedOn);
          if (formData.courierName) dispatchPayload.courierName = formData.courierName;
          if (formData.trackingNumber) dispatchPayload.trackingNumber = formData.trackingNumber;
          if (formData.temperatureAtDispatch) dispatchPayload.temperatureAtDispatch = formData.temperatureAtDispatch;
          if (formData.coldChainMaintained) dispatchPayload.coldChainMaintained = formData.coldChainMaintained;
          if (formData.transferStatus) dispatchPayload.transferStatus = formData.transferStatus;
          if (formData.status) dispatchPayload.status = formData.status;
          if (formData.invoiceNo) dispatchPayload.invoiceNo = formData.invoiceNo;

          if (shouldUpdate) {
            // Update existing transfer (edit mode OR already created in add mode)
            const response = await VaccineDispatchService.update(transferId, dispatchPayload);
            return { success: true, data: response?.data || response, transferId };
          } else {
            // Create new transfer (first time in add mode)
            const response = await VaccineDispatchService.create(dispatchPayload);
            // Extract transferId from various possible response structures
            const newTransferId = response?.data?.id
              || response?.data?._id
              || response?.id
              || response?._id
              || response?.data?.transfer?.id
              || response?.data?.transfer?._id
              || response?.transfer?.id
              || response?.transfer?._id;

            if (!newTransferId) {
              console.error('Could not extract transferId from response:', response);
              throw new Error('Failed to get transfer ID from response');
            }

            return { success: true, data: response?.data || response, transferId: newTransferId };
          }
        }

        // Tab 4 (receive) -> Tab 5 (inspection): Submit receive data
        if (currentTab === 'receive' && nextTab === 'inspection') {
          if (!transferId) {
            throw new Error('Transfer ID is required for receive submission');
          }

          const receivePayload = {};

          // Add fields only if they have values
          if (formData.quantityReceived) receivePayload.quantityReceived = parseInt(formData.quantityReceived);
          if (formData.quantityAccepted) receivePayload.quantityAccepted = parseInt(formData.quantityAccepted);
          if (formData.quantityRejected) receivePayload.quantityRejected = parseInt(formData.quantityRejected);
          if (formData.temperatureAtReceive) receivePayload.temperatureAtReceive = formData.temperatureAtReceive;
          if (formData.qualityCheck) receivePayload.qualityCheck = formData.qualityCheck;
          if (formData.receiveRemarks) receivePayload.receiveRemarks = formData.receiveRemarks;
          if (formData.receivedOn) receivePayload.receivedOn = formatDateToISO(formData.receivedOn);
          if (formData.receivedBy) receivePayload.receivedBy = formData.receivedBy;

          // Always use PUT for receive data
          const response = await VaccineDispatchService.updateReceive(transferId, receivePayload);
          return { success: true, data: response?.data || response, transferId };
        }

        // Tab 5 (inspection) - Final submission
        if (currentTab === 'inspection') {
          if (!transferId) {
            throw new Error('Transfer ID is required for inspection submission');
          }

          const inspectPayload = {};

          // Add fields only if they have values
          if (formData.inspectedOn) inspectPayload.inspectedOn = formatDateToISO(formData.inspectedOn);
          if (formData.inspectedBy) inspectPayload.inspectedBy = formData.inspectedBy;
          if (formData.inspectedQualityCheck) inspectPayload.inspectedQualityCheck = formData.inspectedQualityCheck;
          if (formData.inspectedRemarks) inspectPayload.inspectedRemarks = formData.inspectedRemarks;

          // Always use PUT for inspection data
          const response = await VaccineDispatchService.updateInspect(transferId, inspectPayload);
          return { success: true, data: response?.data || response, transferId, isComplete: true };
        }

        return { success: true, transferId };
      } catch (error) {
        console.error('Tab submission error:', error);
        throw error;
      }
    },

    processFormData: (formData) => {
      // Ensure no negative values for quantity and price fields
      const quantity = Math.max(0, parseInt(formData.quantityDispatched) || 0);
      const price = Math.max(0, parseFloat(formData.price) || 0);
      const calculatedTotal = quantity * price;

      return {
        ...formData,
        quantityDispatched: quantity,
        quantityReceived: formData.quantityReceived ? Math.max(0, parseInt(formData.quantityReceived)) : null,
        quantityAccepted: formData.quantityAccepted ? Math.max(0, parseInt(formData.quantityAccepted)) : null,
        quantityRejected: formData.quantityRejected ? Math.max(0, parseInt(formData.quantityRejected)) : null,
        price: price,
        totalAmount: calculatedTotal > 0 ? calculatedTotal : null,
        temperatureAtDispatch: formData.temperatureAtDispatch || null,
        temperatureAtReceive: formData.temperatureAtReceive || null,
        status: formData.status || 'DISPATCHED'
      };
    }
  }
};
